import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { timeAgo } from "@/lib/format";
import { PencilLine, CheckCircle, XCircle, ArrowRight, ClipboardText, Buildings } from "@phosphor-icons/react";

const fmtValue = (v) => {
  if (v === null || v === undefined || v === "") return <span className="italic text-muted-foreground/70">(empty)</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length === 0 ? <span className="italic text-muted-foreground/70">(empty)</span> : v.map((x) => (typeof x === "object" ? (x.name || x.holder || JSON.stringify(x)) : String(x))).join(", ");
  if (typeof v === "object") return <pre className="text-[10px] font-mono whitespace-pre-wrap max-w-xs">{JSON.stringify(v, null, 2)}</pre>;
  return String(v);
};

export default function AdminPendingEdits() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [changes, setChanges] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/pending-edits");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (companyId) => {
    setNote("");
    try {
      const r = await api.get(`/admin/companies/${companyId}/pending-edit`);
      setChanges(r.data.changes || []);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pending edit");
    }
  };

  const decide = async (action) => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.post(`/admin/companies/${detail.company_id}/pending-edit/${action}`, { review_note: note || null });
      toast.success(action === "approve" ? "Edit approved and published" : "Edit rejected — client will be notified");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Admin · Compliance</div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <PencilLine size={28} weight="fill" className="text-amber-600" />
            Pending client edits
            <span className="text-muted-foreground font-normal">({items.length})</span>
          </h1>
          <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
            Changes submitted by investee company clients are held here for review. The public / investor-facing
            company page continues to show the last approved version until you publish these changes.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead>Company</TableHead>
              <TableHead>Submitted by</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`pending-edit-row-${c.id}`}>
                <TableCell>
                  <Link to={`/companies/${c.id}`} className="flex items-center gap-2 hover:underline min-w-0">
                    {c.logo_url ? (
                      <img src={c.logo_url} alt="" className="h-7 w-7 rounded object-cover" />
                    ) : (
                      <div className="h-7 w-7 rounded bg-secondary grid place-items-center"><Buildings size={13} /></div>
                    )}
                    <span className="text-sm font-medium truncate">{c.name}</span>
                  </Link>
                </TableCell>
                <TableCell className="text-sm">
                  <div className="font-medium">{c.pending_edit_meta?.submitted_by_name}</div>
                  <div className="text-xs text-muted-foreground">{c.pending_edit_meta?.submitted_by_email}</div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{timeAgo(c.pending_edit_meta?.submitted_at)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" onClick={() => openDetail(c.id)} data-testid={`review-edit-${c.id}`}>
                    <ClipboardText size={13} className="mr-1" /> Review
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-12">
                No pending client edits. Any change submitted by a client will appear here for review before being published.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pending-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2">
              <PencilLine size={20} weight="fill" className="text-amber-600" /> Review edit — {detail?.name}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground border-l-2 border-amber-500 pl-3">
                Submitted by <b className="text-foreground">{detail.meta?.submitted_by_name}</b> ({detail.meta?.submitted_by_email})
                · {timeAgo(detail.meta?.submitted_at)}
              </div>

              {changes.length === 0 ? (
                <div className="rounded-lg border border-border bg-secondary/30 p-4 text-sm text-muted-foreground italic">
                  No visible field-level changes detected (the submission may only touch document / metric collections). Approving will still commit the pending edit.
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <div className="px-3 py-2 bg-secondary/40 text-[10px] uppercase tracking-wider font-bold text-muted-foreground flex justify-between">
                    <span>{changes.length} field(s) changed</span>
                    <span>Before → After</span>
                  </div>
                  <div className="divide-y divide-border max-h-[50vh] overflow-y-auto">
                    {changes.map((c) => (
                      <div key={c.field} className="px-3 py-3">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">{c.label}</div>
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-start text-sm">
                          <div className="rounded bg-red-50 dark:bg-red-950/30 border border-red-200/60 dark:border-red-900/40 px-3 py-2 text-red-900/90 dark:text-red-200/90 leading-relaxed whitespace-pre-wrap break-words min-w-0">
                            {fmtValue(c.before)}
                          </div>
                          <ArrowRight size={16} className="text-muted-foreground mt-2 shrink-0" />
                          <div className="rounded bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-900/40 px-3 py-2 text-emerald-900/90 dark:text-emerald-200/90 leading-relaxed whitespace-pre-wrap break-words min-w-0">
                            {fmtValue(c.after)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 font-bold">Review note (optional)</div>
                <Textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Feedback for the client — captured in the audit log and visible on their raise page."
                  data-testid="review-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => decide("reject")}
              disabled={busy}
              data-testid="reject-edit-btn"
              className="text-red-700 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-950/30"
            >
              <XCircle size={14} className="mr-1" /> Reject
            </Button>
            <Button
              onClick={() => decide("approve")}
              disabled={busy}
              data-testid="approve-edit-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CheckCircle size={14} className="mr-1" /> Approve & publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
